import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DoubleTranslatedCustomRichText from '@/components/CustomRichText/DoubleTranslatedCustomRichText';import * as Constants from '@/helpers/constants';
import { getEditorId, removeParagraphTags, tabLowerCase } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import { getHelpRecipients } from '@/redux/Help/help.reducer';
import { setHelpRecipients } from '@/redux/Help/help.action';
import Spinner from '@/components/UI/Spinner/Spinner';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import administrationServices from '@/services/administrationServices';
import { ReduxHelp } from '@/types';
import styles from '../HelpText.module.scss';

interface RecipientsHelpTextProps {
  helpRecipients: ReduxHelp;
  setRecipients: (help: ReduxHelp) => void;
}

const RecipientsHelpText: React.FC<RecipientsHelpTextProps> = (props) => {
  const { t } = useTranslation();
  const tab = tabLowerCase(window.location.pathname.split('/')[4]);
  const [originalValues, setOriginalValues] = useState({});
  const { helpRecipients, setRecipients } = props;
  const isLoading = helpRecipients && !helpRecipients.RECIPIENTS_TAB;
  const { addToast } = useToastContext();
  const RECIPIENTS_TAB = 'RECIPIENTS_TAB';
  const RECIPIENTS_WORKFLOW = 'RECIPIENTS_WORKFLOW';
  const RECIPIENTS_REQUESTER = 'RECIPIENTS_REQUESTER';
  const RECIPIENTS_APPROVER_CAUSER = 'RECIPIENTS_APPROVER_CAUSER';
  const RECIPIENTS_APPROVER_INFORMER = 'RECIPIENTS_APPROVER_INFORMER';
  const RECIPIENTS_PLANTS_APPROVAL_VANS = 'RECIPIENTS_PLANTS_APPROVAL_VANS';
  const RECIPIENTS_QMB_INFORMATION_VANS = 'RECIPIENTS_QMB_INFORMATION_VANS';

  const ID_GENERAL_RECIPIENTS = 'general-recipients';
  const ID_WORKFLOW_RECIPIENTS = 'workflow-recipients';
  const ID_REQUESTER_RECIPIENTS = 'requester-recipients';
  const ID_APPROVER_RECIPIENTS = 'approver-recipients';
  const ID_INFORMER_RECIPIENTS = 'informer-recipients';
  const ID_PLANT_APPROVAL_VANS_RECIPIENTS = 'plant-approval-vans-recipients';
  const ID_QMB_VAN_RECIPIENTS = 'qmb-van-recipients';

  useEffect(() => {
    administrationServices.getHelp(tab)
      .then((data) => {
        const values = helpRecipients && helpRecipients.modified ? helpRecipients : data;
        setRecipients(values);
        setOriginalValues(values);
      })
      .catch((error) => addToast(error.message, 'ERROR'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWysiwygChange = (lang, label, data) => {
    const propValues = { ...helpRecipients[label] };
    const isModified = removeParagraphTags(data) !== removeParagraphTags(originalValues[label][lang]);
    const newState = {
      ...helpRecipients,
      [label]: {
        ...propValues,
        [lang]: data,
      },
      modified: isModified,
    };

    if (isModified) {
      setRecipients(newState as unknown as ReduxHelp);
      setOriginalValues(newState);
    }
  };

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <InfoMsg msg={t('administration.helpText.info')} />
          <AccordionPanel title={t('administration.helpText.generalText')}>
            <DoubleTranslatedCustomRichText
              id={ID_GENERAL_RECIPIENTS}
              title={t('helptext')}
              initialValue={helpRecipients?.RECIPIENTS_TAB}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, RECIPIENTS_TAB, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('recipients.CRWorkflow')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_WORKFLOW_RECIPIENTS}
              title={t('helptext')}
              initialValue={helpRecipients?.RECIPIENTS_WORKFLOW}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, RECIPIENTS_WORKFLOW, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('recipients.requester')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_REQUESTER_RECIPIENTS}
              title={t('helptext')}
              initialValue={helpRecipients?.RECIPIENTS_REQUESTER}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, RECIPIENTS_REQUESTER, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('recipients.approverCauser')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_APPROVER_RECIPIENTS}
              title={t('helptext')}
              initialValue={helpRecipients?.RECIPIENTS_APPROVER_CAUSER}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, RECIPIENTS_APPROVER_CAUSER, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('recipients.approverInformer')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_INFORMER_RECIPIENTS}
              title={t('helptext')}
              initialValue={helpRecipients?.RECIPIENTS_APPROVER_INFORMER}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, RECIPIENTS_APPROVER_INFORMER, newValue)}
            />
          </AccordionPanel>

          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('recipients.vans.plantApproval')} (VANS)`}>
            <DoubleTranslatedCustomRichText
              id={ID_PLANT_APPROVAL_VANS_RECIPIENTS}
              title={t('helptext')}
              initialValue={helpRecipients?.RECIPIENTS_PLANTS_APPROVAL_VANS}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, RECIPIENTS_PLANTS_APPROVAL_VANS, newValue)}
            />
          </AccordionPanel>

          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('recipients.vans.QMBVanInformation')} (VANS)`}>
            <DoubleTranslatedCustomRichText
              id={ID_QMB_VAN_RECIPIENTS}
              title={t('helptext')}
              initialValue={helpRecipients?.RECIPIENTS_QMB_INFORMATION_VANS}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, RECIPIENTS_QMB_INFORMATION_VANS, newValue)}
            />
          </AccordionPanel>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  helpRecipients: getHelpRecipients(state),
});

const actionCreators = {
  setRecipients: setHelpRecipients,
};

export default connect(mapStateToProps, actionCreators)(RecipientsHelpText);
